import { create } from "zustand";
import { makeDefaultProject, makeDefaultWall, newId, type Pricing, type Project, type ProjectInfo, type ReportSettings, type Seismic, type StructureType, type WallInput } from "@tasduvar/engine";
import { projectStore as defaultStore, cloudStore, ConflictError, type ProjectOrigin, type ProjectStore } from "../storage";
import { useProvider } from "../provider/useProvider";
import { applyProviderDefaults } from "../provider/applyDefaults";

const withDefaults = (w: WallInput): WallInput => applyProviderDefaults(w, useProvider.getState().settings.defaults);

/** Yeni projede sorulan deprem girdisi: kapalı ya da harita değerleri (SS, S1, zemin sınıfı → TBDY SDS) */
export type SeismicInit = { enabled: false } | { enabled: true; SS: number; S1: number; siteClass: NonNullable<Seismic["siteClass"]> };
const applySeismic = (w: WallInput, s: SeismicInit): void => {
  if (!s.enabled) { w.seismic.enabled = false; return; }
  Object.assign(w.seismic, { enabled: true, inputMode: "map", SS: s.SS, S1: s.S1, siteClass: s.siteClass });
};

export type SyncState = "idle" | "saving" | "saved" | "offline" | "conflict" | "error";

/** Geri al/Yinele anlık görüntüsü: proje nesneleri değişmez (her güncelleme kopya üretir), bu yüzden başvuru saklamak yeter. */
interface Snap { project: Project; selectedWallId: string | null }
const HISTORY_MAX = 100;
/** Aynı hedefe (ör. aynı duvar) bu süre içinde gelen ardışık değişiklikler (yazma, sürükleme) tek adım sayılır. */
export const HISTORY_COALESCE_MS = 1000;
let lastKey: string | null = null, lastAt = 0;

export interface ProjectState {
  /** `dirty`: otomatik kayıt (taslak/bulut) bekliyor — yazılınca temizlenir. */
  project: Project | null; selectedWallId: string | null; dirty: boolean; lastSavedAt: string | null;
  /**
   * AutoCAD "değiştirildi" (*) durumu: açılıştan ya da son açık kayıttan (Kaydet / Ctrl+S) bu yana değişiklik var mı?
   * Otomatik kayıt bunu TEMİZLEMEZ (yalnız çökme/taslak koruması); kapatırken "Kaydet / Kaydetme / İptal" buna bakar.
   * `baseline` o kayıt noktasındaki proje nesnesi (Kaydetme → buna döner); `openedAt` açılış anı (sekme eşgüdümü).
   */
  baseline: Project | null; modified: boolean; openedAt: number;
  /** Aynı proje başka sekmede düzenleniyor: other = orada zaten açıktı · taken = o sekme düzenlemeyi devraldı (bu sekme salt okunur). */
  tabLock: "other" | "taken" | null;
  /** local: bu cihaz · cloud: sunucu · shared: paylaşım bağlantısı (salt okunur). */
  origin: ProjectOrigin; readOnly: boolean;
  sync: SyncState; syncError: string | null; conflict: { server: { updatedAt: string; digest: string } } | null;
  newProject(info: Partial<ProjectInfo>, origin?: ProjectOrigin, seismic?: SeismicInit): Project;
  openProject(p: Project, origin?: ProjectOrigin, readOnly?: boolean): void;
  closeProject(): void;
  setSync(sync: SyncState, error?: string | null): void;
  setConflict(c: ProjectState["conflict"]): void;
  setOrigin(origin: ProjectOrigin): void;
  updateInfo(patch: Partial<ProjectInfo>): void;
  updateReportSettings(next: ReportSettings): void;
  updatePricing(next: Pricing): void;
  updateWall(id: string, recipe: (w: WallInput) => void): void;
  /**
   * Duvar reçetesi + künye yaması TEK geri alma adımında (sihirbaz: rapordan il/ilçe/ada/parsel, konum). Öncesi ve sonrası
   * birleştirilmez (kendi adımıdır); etkisiz kısım dokunulmaz, ikisi de etkisizse hiçbir şey yazılmaz. Salt okunurda yazmaz.
   */
  updateWallAndInfo(id: string, recipe: (w: WallInput) => void, infoPatch: Partial<ProjectInfo>): void;
  addWall(type?: StructureType): WallInput; copyWall(id: string): WallInput; removeWall(id: string): void; renameWall(id: string, name: string): void;
  selectWall(id: string): void;
  markSaved(): void;
  /** Açık kayıt noktası: `p` (yazılan proje) yeni baseline olur. */
  commitSaved(p: Project): void;
  /** Proje geneli Geri al / Yinele (duvar, proje bilgisi, fiyat, rapor ayarı değişiklikleri). */
  past: Snap[]; future: Snap[];
  undo(): void; redo(): void;
  /** Sonraki değişikliği öncekiyle birleştirmeden yeni geri alma adımı olarak başlatır. */
  checkpoint(): void;
}

const touch = (p: Project): Project => ({ ...p, updatedAt: new Date().toISOString() });

/** Reçeteyi `id` duvarının kopyasına uygular; değişmediyse null (etkisiz güncelleme) */
function patchWall(p: Project, id: string, recipe: (w: WallInput) => void): WallInput[] | null {
  let changed = false;
  const walls = p.walls.map((w) => { if (w.id !== id) return w; const c = structuredClone(w); recipe(c); if (JSON.stringify(c) === JSON.stringify(w)) return w; changed = true; return c; });
  return changed ? walls : null;
}
/** Künyeye gerçekten yeni değer getiren alanlar (aynı değer → yok sayılır) */
function infoDiff(info: ProjectInfo, patch: Partial<ProjectInfo>): Partial<ProjectInfo> {
  return Object.fromEntries(Object.entries(patch).filter(([k, v]) => v !== undefined && JSON.stringify(info[k as keyof ProjectInfo]) !== JSON.stringify(v)));
}

const noHistory = (): Pick<ProjectState, "past" | "future"> => { lastKey = null; return { past: [], future: [] }; };

export const useProjectStore = create<ProjectState>((set, get) => {
  /** Değişiklikten önceki hâli geçmişe yazar; `key` aynıysa ve süre dolmadıysa önceki adıma katılır. */
  const record = (key: string): Pick<ProjectState, "past" | "future"> => {
    const s = get(), now = Date.now();
    const merge = key === lastKey && now - lastAt < HISTORY_COALESCE_MS && s.past.length > 0;
    lastKey = key; lastAt = now;
    if (merge || !s.project) return { past: s.past, future: [] };
    return { past: [...s.past, { project: s.project, selectedWallId: s.selectedWallId }].slice(-HISTORY_MAX), future: [] };
  };
  /** Salt okunur (paylaşım, abonelik, başka sekme) projede değişiklik yapılmaz. */
  const editable = (): Project | null => (get().readOnly ? null : get().project);
  const travel = (from: "past" | "future"): void => {
    const s = get(); const stack = s[from]; const snap = stack[stack.length - 1];
    if (!snap || !s.project || s.readOnly) return;
    lastKey = null;
    const here: Snap = { project: s.project, selectedWallId: s.selectedWallId };
    const sel = snap.project.walls.some((w) => w.id === s.selectedWallId) ? s.selectedWallId : (snap.selectedWallId ?? snap.project.walls[0]?.id ?? null);
    set({
      // kayıt noktasına kadar geri alındıysa artık "değiştirildi" değil
      project: touch(snap.project), selectedWallId: sel, dirty: true, modified: snap.project !== s.baseline,
      past: from === "past" ? stack.slice(0, -1) : [...s.past, here],
      future: from === "future" ? stack.slice(0, -1) : [...s.future, here],
    });
  };
  return {
  project: null, selectedWallId: null, dirty: false, lastSavedAt: null, baseline: null, modified: false, openedAt: 0, tabLock: null,
  origin: "local", readOnly: false, sync: "idle", syncError: null, conflict: null,
  past: [], future: [],
  undo() { travel("past"); },
  redo() { travel("future"); },
  checkpoint() { lastKey = null; },
  newProject(info, origin = "local", seismic) {
    const p = makeDefaultProject(info);
    p.walls = p.walls.map(withDefaults);
    if (seismic) for (const w of p.walls) applySeismic(w, seismic);
    const pd = useProvider.getState().settings.defaults.pricing;
    if (pd) p.pricing = { ...p.pricing, ...structuredClone(pd) };   // sağlayıcı poz tablosu / fiyat yılı varsayılanı
    // Yeni proje oluşturulunca depoya yazılır (künye = ilk kayıt): kayıt noktası oluşturma anı. Hiç açık kaydedilmemiş yeni
    // projede "Kaydetme" projeyi SİLMEZ, bu ilk hâline döndürür (künye ve deprem girdisi korunur; bkz. revertToBaseline).
    set({ project: p, selectedWallId: p.walls[0]!.id, dirty: true, lastSavedAt: null, baseline: p, modified: false, openedAt: Date.now(), tabLock: null, origin, readOnly: false, sync: "idle", syncError: null, conflict: null, ...noHistory() });
    return p;
  },
  openProject(p, origin = "local", readOnly = origin === "shared") {
    set({ project: p, selectedWallId: p.walls[0]?.id ?? null, dirty: false, lastSavedAt: p.updatedAt, baseline: p, modified: false, openedAt: Date.now(), tabLock: null, origin, readOnly, sync: "idle", syncError: null, conflict: null, ...noHistory() });
  },
  closeProject() { set({ project: null, selectedWallId: null, dirty: false, lastSavedAt: null, baseline: null, modified: false, openedAt: 0, tabLock: null, origin: "local", readOnly: false, sync: "idle", syncError: null, conflict: null, ...noHistory() }); },
  setSync(sync, error = null) { set({ sync, syncError: error }); },
  setConflict(conflict) { set({ conflict, sync: conflict ? "conflict" : "idle" }); },
  setOrigin(origin) { set({ origin, readOnly: origin === "shared" }); },
  updateInfo(patch) {
    const p = editable(); if (!p) return;
    set({ ...record(`info:${Object.keys(patch).join(",")}`), project: touch({ ...p, info: { ...p.info, ...patch } }), dirty: true, modified: true });
  },
  updateReportSettings(next) {
    const p = editable(); if (!p) return;
    set({ ...record("report"), project: touch({ ...p, reportSettings: next }), dirty: true, modified: true });
  },
  updatePricing(next) {
    const p = editable(); if (!p) return;
    set({ ...record("pricing"), project: touch({ ...p, pricing: next }), dirty: true, modified: true });
  },
  updateWall(id, recipe) {
    const p = editable(); if (!p) return;
    const walls = patchWall(p, id, recipe);
    if (!walls) return;   // etkisiz güncelleme: kirletme, geri alma adımı açma
    set({ ...record(`wall:${id}`), project: touch({ ...p, walls }), dirty: true, modified: true });
  },
  updateWallAndInfo(id, recipe, infoPatch) {
    const p = editable(); if (!p) return;
    const walls = patchWall(p, id, recipe), info = infoDiff(p.info, infoPatch);
    const infoChanged = Object.keys(info).length > 0;
    if (!walls && !infoChanged) return;
    lastKey = null;   // önceki yazmayla birleşmesin
    set({ ...record(`wizard:${id}`), project: touch({ ...p, walls: walls ?? p.walls, info: infoChanged ? { ...p.info, ...info } : p.info }), dirty: true, modified: true });
    lastKey = null;   // sonraki yazma da bu adıma katılmasın
  },
  addWall(type = "masonry-gravity") {
    const p = get().project; if (!p) throw new Error("Açık proje yok");
    if (get().readOnly) throw new Error("Salt okunur proje");
    const w = withDefaults(makeDefaultWall(`Duvar-${p.walls.length + 1}`, type));
    // Deprem girdisi (konum, zemin sınıfı) proje genelidir: yeni duvar seçili duvarınkini alır
    const from = p.walls.find((x) => x.id === get().selectedWallId) ?? p.walls[0];
    if (from) w.seismic = structuredClone(from.seismic);
    set({ ...record(`add:${w.id}`), project: touch({ ...p, walls: [...p.walls, w] }), selectedWallId: w.id, dirty: true, modified: true });
    return w;
  },
  copyWall(id) {
    const p = get().project; if (!p) throw new Error("Açık proje yok");
    if (get().readOnly) throw new Error("Salt okunur proje");
    const src = p.walls.find((w) => w.id === id); if (!src) throw new Error("Duvar bulunamadı");
    const w: WallInput = { ...structuredClone(src), id: newId("wall"), name: `${src.name} (kopya)` };
    set({ ...record(`add:${w.id}`), project: touch({ ...p, walls: [...p.walls, w] }), selectedWallId: w.id, dirty: true, modified: true });
    return w;
  },
  removeWall(id) {
    const p = editable(); if (!p || p.walls.length <= 1) return;
    const walls = p.walls.filter((w) => w.id !== id);
    const sel = get().selectedWallId === id ? walls[0]!.id : get().selectedWallId;
    set({ ...record(`remove:${id}`), project: touch({ ...p, walls }), selectedWallId: sel, dirty: true, modified: true });
  },
  renameWall(id, name) { get().updateWall(id, (w) => { w.name = name.trim() || w.name; }); },
  selectWall(id) { set({ selectedWallId: id }); },
  markSaved() { set({ dirty: false, lastSavedAt: new Date().toISOString() }); },
  commitSaved(p) { lastKey = null; set({ baseline: p, modified: get().project !== p }); },
  };
});

/**
 * dirty olduktan sonra kökene göre depoya yazar: yerel taslak `delayMs` (1 s), bulut `cloudDelayMs` (5 s).
 * Bulutta 409 → `conflict` (diyalog), ağ yok → `offline` (kuyruk), 402/diğer → `error`. Durdurucu döner.
 */
export function startAutosave(store: ProjectStore = defaultStore, delayMs = 1000, cloudDelayMs = 5000, cloud: ProjectStore = cloudStore): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const unsub = useProjectStore.subscribe((s, prev) => {
    if (!s.dirty || !s.project || s.readOnly || s.conflict) return;
    if (s.project === prev.project && s.dirty === prev.dirty) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void saveNow(store, cloud), s.origin === "cloud" ? cloudDelayMs : delayMs);
  });
  return () => { unsub(); if (timer) clearTimeout(timer); };
}

/**
 * Depo yazımları sırayla yapılır (otomatik kayıt, Kaydet, Kaydetme, kapatırken boşaltma): eşzamanlı iki bulut PUT'u aynı
 * `baseUpdatedAt` ile gidip kendi kendine 409 çakışması üretmesin.
 */
let chain: Promise<unknown> = Promise.resolve();
function serial<T>(job: () => Promise<T>): Promise<T> { const run = chain.then(job); chain = run.catch(() => undefined); return run; }

/** `st` anındaki projeyi (dirty ise) kökenine yazar; store yalnız aynı proje hâlâ açıksa güncellenir. Başarı → true. */
async function writeState(st: ProjectState, store: ProjectStore, cloud: ProjectStore, force: boolean): Promise<boolean> {
  const cur = st.project;
  if (!cur || st.readOnly || st.origin === "shared" || (!st.dirty && !force)) return true;
  const same = (): ProjectState | null => { const after = useProjectStore.getState(); return after.project?.id === cur.id ? after : null; };
  if (st.origin !== "cloud") { await store.save(cur); const after = same(); if (after?.project === cur) after.markSaved(); return true; }
  same()?.setSync("saving");
  try {
    const r = await cloud.save(cur, { force });
    const after = same();
    if (after) { if (after.project === cur) after.markSaved(); after.setConflict(null); after.setSync(r && r.queued ? "offline" : "saved"); }
    return true;
  } catch (e) {
    const after = same();
    if (after) { if (e instanceof ConflictError) after.setConflict({ server: e.server }); else after.setSync("error", e instanceof Error ? e.message : "Kaydedilemedi"); }
    return false;
  }
}

/** Bekleyen değişikliği hemen yazar (otomatik kayıt ve "Üzerine yaz" için ortak yol); sıra gelince o anki durum yazılır. */
export function saveNow(store: ProjectStore = defaultStore, cloud: ProjectStore = cloudStore, force = false): Promise<boolean> {
  return serial(() => writeState(useProjectStore.getState(), store, cloud, force));
}

/** Verilen anlık durumu yazar — proje kapatılırken/başka sekmeye devredilirken bekleyen otomatik kayıt kaybolmasın. */
export function saveSnapshot(st: ProjectState, store: ProjectStore = defaultStore, cloud: ProjectStore = cloudStore): Promise<boolean> {
  return serial(() => writeState(st, store, cloud, false));
}

/**
 * Açık kayıt (Kaydet / Ctrl+S / KAYDET; kapatma diyaloğunda "Kaydet"): bekleyen değişikliği yazar (bulutta normal kayıt →
 * sürüm geçmişine girer) ve kayıt noktasını buraya taşır → "değiştirildi" (*) temizlenir. Başarılıysa true.
 */
export function commitSave(store: ProjectStore = defaultStore, cloud: ProjectStore = cloudStore): Promise<boolean> {
  return serial(async () => {
    const st = useProjectStore.getState(), cur = st.project;
    if (!cur || st.readOnly || st.origin === "shared") return false;
    const ok = await writeState(st, store, cloud, false);
    const after = useProjectStore.getState();
    if (ok && after.project?.id === cur.id) after.commitSaved(cur);
    return ok;
  });
}

/**
 * "Kaydetme": kayıt noktasından (açılış ya da son açık kayıt) bu yana yapılan değişiklikleri atar. Otomatik kayıt onları
 * depoya çoktan yazmış olabilir → baseline yeniden yazılır ki taslak/bulut kopyası da ona dönsün. Yeni projede baseline
 * oluşturma anıdır: proje SİLİNMEZ, oluşturulduğu hâle (künye + deprem girdisi) döner. Başarılıysa true (hata → syncError).
 */
export function revertToBaseline(store: ProjectStore = defaultStore, cloud: ProjectStore = cloudStore): Promise<boolean> {
  const st = useProjectStore.getState(), base = st.baseline;
  if (!st.project || !base || st.readOnly || st.origin === "shared") return Promise.resolve(true);
  // Önce store baseline'a döner: bu arada otomatik kayıt zamanlayıcısı çalışırsa değiştirilmiş hâli yazmasın
  useProjectStore.setState({ project: base, selectedWallId: base.walls.some((w) => w.id === st.selectedWallId) ? st.selectedWallId : base.walls[0]?.id ?? null, dirty: false, modified: false, ...noHistory() });
  return serial(async () => {
    try { await (st.origin === "cloud" ? cloud : store).save(base); return true; }
    catch (e) { const after = useProjectStore.getState(); if (after.project?.id === base.id) after.setSync("error", e instanceof ConflictError ? "Proje sunucuda değişmiş" : e instanceof Error ? e.message : "Kaydedilemedi"); return false; }
  });
}

/** Kapatırken sorulmalı mı? (değiştirildi · düzenlenebilir · paylaşım değil) */
export function isModified(s: ProjectState = useProjectStore.getState()): boolean {
  return !!s.project && s.modified && !s.readOnly && s.origin !== "shared";
}

export function useSelectedWall(): WallInput | null {
  return useProjectStore((s) => s.project?.walls.find((w) => w.id === s.selectedWallId) ?? null);
}
